import styles from './instoreCollection.module.scss';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDeviceContextViewport } from '../../contexts/Device/DeviceContext';
import { Pagination, PaginationMobile } from '../../ds';
import EmptySearchResults from '../EmptySearchResults/index';
import { ReactComponent as SalesIcon } from './assets/sales-icon.svg';
import HeaderSection from './HeaderSection';
import ActionBarSection from './ActionBarSection';
import TableSection from './TableSection';
import OrdersTable from './OrdersTable';
import PaginationSection from './PaginationSection';
import { useQueryOrders } from './hooks/useQueryOrders';
import { useQueryParams } from '../../Utils/useQueryParams';
import ErrorModal from '../ErrorModal/ErrorModal';
import usePusherChannel from '../Pusher/hooks/usePusherChannel';
import { useAuth } from '../../Utils/Auth/use-auth';
import { SelectStores } from '../StoreSelection/SelectStores';
import ModalCancel from './modal/ModalCancel';
import MerchantPaymentsService from '../../services/MerchantPaymentsService';
import AnalyticsEvent from '../../services/analytics/AnalyticsEvent';
import {
  notApplicableOrderStaffId,
  REFUND_CREATE_FAIL,
} from '../../Utils/constants';
import isValidTranslation from '../../Utils/isValidTranslation';
import { useLoaderContext } from '../../contexts/Loader/LoaderContext';
import { useToastContext } from '../../contexts/Toast/ToastContext';
import { useTranslation } from 'react-i18next';
import ModalContainer from './modal/container';
import { useStore } from '../../services/store';
import StaticQrModal from './modal/StaticQrModal';
import { useStaticQr } from './hooks/useStaticQr';
import { isFeatureEnabled } from '../../Utils/Feature/feature_flag';

function InstoreCollection() {
  const { isMobile } = useDeviceContextViewport();
  const { setIsLoaderOpen } = useLoaderContext();
  const addToast = useToastContext();
  const { t } = useTranslation();
  const history = useHistory();
  const queryParams = useQueryParams();
  const authData = useAuth();
  const { user } = authData;

  const { qrCodeStore } = useStore();

  const modalRef = useRef();

  const [showCancelOrder, setShowCancelOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOpenalreadyPaidErrorInfoModal, setIsOpenAlreadyPaidErrorInfoModal] =
    useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showStaticQrModal, setShowStaticQrModal] = useState(false);

  const PaginationComponent = isMobile ? PaginationMobile : Pagination;
  const isSingleStore = user.stores.length === 1;
  const isMultiStore = user.stores.length > 1;
  const [storeId, setStoreId] = useState(() =>
    isSingleStore ? user.stores[0].store_id : queryParams.get('item') || null
  );

  const staticQrData = useStaticQr(storeId);

  useEffect(() => {
    if (storeId && qrCodeStore.storeId !== storeId) {
      qrCodeStore.setStoreId(storeId);
      if (modalRef.current && modalRef.current.resetFlow) {
        modalRef.current.resetFlow();
      }
    }
  }, [storeId, qrCodeStore]);
  const queryOrders = useQueryOrders({
    storeId: storeId ?? '',
    enabled: Boolean(storeId),
  });
  const hasStoreSelected = Boolean(storeId);
  const ordersData = queryOrders.queryResult.data?.orders ?? [];
  const hasData = ordersData.length > 0;

  // Helper functions
  const readOnlyPermission = () => {
    return Boolean(user.permission_scopes?.at(0)?.scopes?.length === 1);
  };

  const goDetails = async (orderId, selectedStoreId) => {
    if (isMultiStore) {
      await history.replace(`/orders?item=${selectedStoreId}`);
    }
    history.push(`/orders/${orderId}/${selectedStoreId}`);
    const contentArea = document.getElementById('content-area');
    contentArea.scrollTop = 0;
  };

  const handleShowStaticQr = () => {
    setShowStaticQrModal(true);
  };

  const handleCloseStaticQr = () => {
    setShowStaticQrModal(false);
  };

  // Event handlers
  const fetchRequestRefund = async () => {
    setShowCancelOrder(false);
    setIsLoaderOpen(true);

    try {
      const orderResponse = await MerchantPaymentsService().getOrderDetail(
        selectedOrder.order_id,
        selectedOrder.store_id
      );

      await MerchantPaymentsService().requestOrderRefund({
        order_id: selectedOrder.order_id,
        staff_id:
          orderResponse.staff_id !== notApplicableOrderStaffId
            ? orderResponse.staff_id
            : '',
        amount: parseFloat(orderResponse.total),
      });
    } catch (error) {
      const responseMessage = error?.response?.data?.message;
      AnalyticsEvent().errorTrack({
        errorType: 'payment-order-details-request-refund-error',
        errorDescription: error,
      });

      if (responseMessage === REFUND_CREATE_FAIL) {
        setIsOpenAlreadyPaidErrorInfoModal(true);
      } else {
        var message = 'Ocurrió un error, ¿Podrías intentarlo de nuevo?';
        const translationPrefix = 'refund.';
        let responseMessageTranslation = '';

        if (responseMessage) {
          responseMessageTranslation = t(translationPrefix + responseMessage, {
            nsSeparator: false,
          });
        }

        if (
          responseMessageTranslation &&
          isValidTranslation(translationPrefix, responseMessageTranslation)
        ) {
          message = responseMessageTranslation;
        }

        addToast(message, 'error');
        setIsLoaderOpen(false);
      }

      console.log(error);
      setIsLoaderOpen(false);
    } finally {
      queryOrders.queryResult.refetch();
      setIsLoaderOpen(false);
    }
  };

  // Pusher integration
  const handlerPusherCallback = useCallback(() => {
    queryOrders.queryResult.refetch();
  }, [queryOrders]);

  usePusherChannel(
    storeId ? `channel_sales_view_${storeId}` : '',
    'order_status_updated',
    handlerPusherCallback
  );

  const emptyComponent =
    !isSingleStore && !hasStoreSelected ? (
      <EmptySearchResults
        header='Sin resultados'
        subHeader='Selecciona una sucursal para consultar las ventas.'
      />
    ) : !hasData ? (
      <EmptySearchResults
        icon={<SalesIcon />}
        header='¡Todo listo para tu primera venta!'
        subHeader='Aquí verás las órdenes del día en cuanto empieces a vender.'
      />
    ) : null;

  return (
    <div className={styles.instoreCollection}>
      <HeaderSection
        isMobile={isMobile}
        storeSelection={
          isMultiStore && (
            <SelectStores
              type='text'
              name='sucursal'
              stores={user.stores.map(store => ({
                value: store.store_id,
                label: store.name,
              }))}
              onChange={selectedOption => {
                setStoreId(selectedOption.value);
                queryOrders.updateFilters({
                  storeId: selectedOption.value,
                  page: 1,
                  pageSize: 20,
                  search: '',
                });
              }}
            />
          )
        }
      />

      <ActionBarSection
        onCreateOrder={() => setShowPurchaseModal(true)}
        onShowStaticQr={handleShowStaticQr}
        hasStoreSelected={hasStoreSelected}
        hasStaticQr={
          staticQrData.hasQr && isFeatureEnabled('instore_qr_static_code_sales')
        }
      />

      <TableSection
        isMobile={isMobile}
        onRefresh={() => {
          if (isSingleStore || hasStoreSelected) {
            queryOrders.queryResult.refetch();
          }
        }}
      >
        <>
          <OrdersTable
            ordersData={ordersData}
            readOnlyPermission={readOnlyPermission}
            goDetails={goDetails}
            setSelecedOrder={setSelectedOrder}
            setShowCancelOrder={setShowCancelOrder}
          />

          {!queryOrders.queryResult.isFetching && emptyComponent}

          {hasData && (
            <PaginationSection
              queryResult={queryOrders.queryResult}
              changePage={queryOrders.changePage}
              PaginationComponent={PaginationComponent}
            />
          )}
        </>
      </TableSection>

      <ErrorModal
        heading=''
        description={
          <div>
            {
              'Para cancelar esta transacción que ya fue liquidada por el cliente, envía ID de pago, monto y tipo de cancelación (parcial/total) a '
            }
            <a
              href='mailto:cancelacioneskueskipay@kueski.com'
              className='k-ds-text-color-interactive-primary'
            >
              cancelacioneskueskipay@kueski.com
            </a>
            .
          </div>
        }
        defaultOpened={isOpenalreadyPaidErrorInfoModal}
        close={setIsOpenAlreadyPaidErrorInfoModal}
        showClose
      />
      <ModalCancel
        isOpen={showCancelOrder}
        onClose={() => {
          setShowCancelOrder(false);
          setSelectedOrder(null);
        }}
        onClick={async () => await fetchRequestRefund()}
      />
      <ModalContainer
        ref={modalRef}
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
      />
      <StaticQrModal
        isOpen={
          showStaticQrModal && isFeatureEnabled('instore_qr_static_code_sales')
        }
        onClose={handleCloseStaticQr}
        qrCode={staticQrData.code}
        qrUrl={staticQrData.url}
        storeId={storeId}
        storeName={user.stores.find(store => store.store_id === storeId)?.name}
      />
    </div>
  );
}

export default InstoreCollection;
